import api from '@/services/api';
import type { GlobalSearchResponse } from './types';

/**
 * `GET /api/v1/search/` -- the cross-module search backing the header bar.
 *
 * The `signal` is passed straight to axios so a superseded keystroke's request
 * is actually aborted rather than merely ignored: the bar fires on typing, and
 * a queue of dead requests holding sockets open is what makes search feel slow
 * on a mobile connection.
 */
export async function searchGlobal(
  term: string,
  signal?: AbortSignal,
  limit?: number
): Promise<GlobalSearchResponse> {
  const params: Record<string, string> = { q: term };
  if (limit != null) params.limit = String(limit);

  const response = await api.get<GlobalSearchResponse>('/search/', { params, signal });
  return response.data;
}

/**
 * True for the "we cancelled this on purpose" family of errors.
 *
 * axios reports an aborted request as `ERR_CANCELED` / `CanceledError`; a raw
 * fetch would raise a DOMException named `AbortError`. Neither is a failure the
 * user should ever see, so the hook must not surface them as an error state.
 */
export function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { code?: string; name?: string; message?: string };
  return (
    candidate.code === 'ERR_CANCELED' ||
    candidate.name === 'CanceledError' ||
    candidate.name === 'AbortError'
  );
}
